import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {getLocationFromAddress} from '../../../shared/misc/location-helpers';
import {getClient, updateClient} from '../clients.service';
import {createClientContact, deleteClientContact, getClientContacts, updateClientContact} from '../client-contacts.service';
import {ClientDto} from '../dtos/client.dto';
import {ClientContactDto} from '../dtos/client-contact.dto';
import {ClientRatingDto, getClientRating, setClientRating, setClientReview} from './client-profile.service';
import {
  addressLine,
  CONTACT_KINDS,
  ClientFields,
  ContactEntry,
  ContactKind,
  entriesFrom,
  isMainKey,
  mainEntry,
  ParsedAddress,
  planContactCommit,
  withParsedAddress,
} from './contact-entries';

/*
 * The client card's state and its saves, for every page that shows the card
 * (SQ-575): the client's own page and the records that carry a client (the
 * estimate appointment, SQ-595). Edit mode's Save writes the client's own
 * fields and brings Additional Client Info in line with its entries; as on
 * Android there is no discard, so leaving a page while editing saves what
 * Save would have.
 */

export const NO_FIELDS: ClientFields = {name: '', phoneNumber: '', email: '', address: '', city: '', state: '', postalCode: '', lat: 0, lng: 0};

export const fieldsOf = (client: ClientDto): ClientFields => ({
  name: client.name ?? '',
  phoneNumber: client.phoneNumber ?? '',
  email: client.email ?? '',
  address: client.address ?? '',
  city: client.city ?? '',
  state: client.state ?? '',
  postalCode: client.postalCode ?? '',
  lat: client.lat ?? 0,
  lng: client.lng ?? 0,
});

const sameFields = (a: ClientFields, b: ClientFields) =>
  (Object.keys(a) as (keyof ClientFields)[]).every(key => a[key] === b[key]);

/** The client's own address on one line: what is looked up, and what a point belongs to. */
const lineOf = (f: ClientFields) => addressLine(mainEntry(f, 'address'));

export const NAME_REQUIRED = 'Client name is required';
export const ADDRESS_NOT_FOUND = "Couldn't find that address. Check it, or pick one of the suggestions.";

/** Everything a save needs, read at the moment it runs. */
export interface ClientDraft {
  client?: ClientDto;
  fields: ClientFields;
  contacts: ClientContactDto[];
  entries: ContactEntry[];
}

export interface ClientEditingOptions {
  /** Whether the card is in edit mode now: leaving the page then saves it. */
  editing: boolean;
  /** Shows a save's refusal on the page. */
  onError: (message: string) => void;
  /** The client was saved: whatever lists it is out of date. */
  onChanged: () => void;
}

/** `clientId` undefined: a client that does not exist yet (the new client page), or a record still loading. */
export const useClientEditing = (clientId: number | undefined, {editing, onError, onChanged}: ClientEditingOptions) => {
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientDto>();
  const [fields, setFields] = useState<ClientFields>(NO_FIELDS);
  const [contacts, setContacts] = useState<ClientContactDto[]>([]);
  const [entries, setEntries] = useState<ContactEntry[]>([]);
  // The create buttons ask about the entries, so a page waits for them (a failed read counts as none).
  const [contactsRead, setContactsRead] = useState(clientId === undefined);
  const [rating, setRating] = useState<ClientRatingDto>();
  const [loadError, setLoadError] = useState<string>();

  const newEntries = useRef(0);
  const draft = useRef<ClientDraft>({fields, contacts, entries});
  draft.current = {client, fields, contacts, entries};
  const editingNow = useRef(editing);
  editingNow.current = editing;
  // Deleted, or handed on to another page: nothing is left to save on the way out.
  const settled = useRef(false);
  const changed = useRef(onChanged);
  changed.current = onChanged;
  // The address line the client's lat/lng belong to: the saved one, or the last suggestion picked.
  // A line typed since has no point yet and is looked up before it is saved.
  const pinnedLine = useRef('');

  useEffect(() => {
    if (clientId === undefined) return;
    let active = true;
    setContactsRead(false);
    getClient(navigate, clientId)
      .then(loaded => {
        if (!active) return;
        setClient(loaded);
        setFields(fieldsOf(loaded));
        pinnedLine.current = lineOf(fieldsOf(loaded));
      })
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        logError('Error getting client', {clientId}, e as Error);
        setLoadError(`Couldn't load this client: ${e}`);
      });
    getClientContacts(navigate, clientId)
      .then(rows => {
        if (!active) return;
        setContacts(rows);
        setEntries(entriesFrom(rows));
      })
      .catch(() => undefined)
      .finally(() => active && setContactsRead(true));
    // Without a rating the card simply has no rating block.
    getClientRating(navigate, clientId).then(r => active && setRating(r)).catch(() => undefined);
    return () => { active = false; };
  }, [clientId, navigate]);

  // ---- Save -------------------------------------------------------------------------

  /**
   * The point for the address being saved: the one it has when the line is
   * the one that point belongs to, else the typed line looked up. Without
   * this a typed address kept the old pin, and Create Appointment copied it
   * onto the appointment. Throws with the message to show.
   */
  const locate = useCallback(async (now: ClientFields): Promise<ClientFields> => {
    const line = lineOf(now);
    if (!line || line === pinnedLine.current) return now;
    const found = await getLocationFromAddress(line).catch(() => null);
    if (!found) throw new Error(ADDRESS_NOT_FOUND);
    return {...now, lat: found.lat, lng: found.lng};
  }, []);

  /** Writes the client's own fields when they have changed. Throws with the message to show. */
  const persistClient = useCallback(async ({client: saved, fields: typed}: ClientDraft): Promise<ClientDto | undefined> => {
    if (!saved || sameFields(typed, fieldsOf(saved))) return saved;
    if (!typed.name.trim()) throw new Error(NAME_REQUIRED);
    const now = await locate(typed);
    const moved = now.lat !== saved.lat || now.lng !== saved.lng;
    return await updateClient(navigate, saved.id, {
      name: now.name.trim(),
      address: now.address.trim(),
      city: now.city.trim(),
      state: now.state.trim(),
      postalCode: now.postalCode.trim(),
      email: now.email.trim(),
      phoneNumber: now.phoneNumber.trim(),
      // Only a real pin, and only a new one.
      ...(moved && now.lat !== 0 && now.lng !== 0 ? {lat: now.lat, lng: now.lng} : {}),
    });
  }, [navigate, locate]);

  /**
   * Brings the saved rows in line with the entries: new ones are created,
   * edited ones updated, emptied ones dropped. Then re-reads the rows, so
   * blank entries that were never saved go too.
   */
  const commitEntries = useCallback(async (id: number, {contacts: rows, entries: now}: ClientDraft): Promise<ClientContactDto[]> => {
    const plan = planContactCommit(rows, now);
    if (plan.creates.length + plan.updates.length + plan.deletes.length === 0) return rows;
    for (const rowId of plan.deletes) await deleteClientContact(navigate, id, rowId);
    for (const {id: rowId, request} of plan.updates) await updateClientContact(navigate, id, rowId, request);
    for (const request of plan.creates) await createClientContact(navigate, id, request);
    return await getClientContacts(navigate, id);
  }, [navigate]);

  /** One press of Save, or its equal before another action. Null: something was refused and the message is up. */
  const saveAll = async (): Promise<ClientDraft | null> => {
    const now = draft.current;
    if (!now.client) return null;
    let saved: ClientDto;
    try {
      saved = (await persistClient(now))!;
    } catch (e) {
      if (!isAuthRedirectError(e)) onError(e instanceof Error && [NAME_REQUIRED, ADDRESS_NOT_FOUND].includes(e.message) ? e.message : 'Failed to save the client');
      return null;
    }
    setClient(saved);
    setFields(fieldsOf(saved));
    pinnedLine.current = lineOf(fieldsOf(saved));
    let rows: ClientContactDto[];
    try {
      rows = await commitEntries(saved.id, now);
    } catch (e) {
      // The card stays in edit mode, entries intact, so nothing typed is lost.
      if (!isAuthRedirectError(e)) onError('Failed to save Additional Client Info');
      return null;
    }
    const savedEntries = entriesFrom(rows);
    setContacts(rows);
    setEntries(savedEntries);
    return {client: saved, fields: fieldsOf(saved), contacts: rows, entries: savedEntries};
  };

  // Leaving the page while editing commits what Save would have, as Android does.
  useEffect(() => () => {
    const now = draft.current;
    if (!editingNow.current || settled.current || !now.client) return;
    const id = now.client.id;
    persistClient(now)
      .then(() => commitEntries(id, now))
      .then(() => changed.current())
      .catch(e => logError('Saving the client on the way out failed', {clientId: id}, e as Error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- The card's edits -----------------------------------------------------------------

  const changeEntry = (key: string, update: (entry: ContactEntry) => ContactEntry) => {
    if (!isMainKey(key)) {
      setEntries(list => list.map(entry => entry.key === key ? update(entry) : entry));
      return;
    }
    const kind = CONTACT_KINDS.find(k => key.endsWith(k))!;
    setFields(current => {
      const e = update(mainEntry(current, kind));
      switch (kind) {
        case 'name': return {...current, name: e.value};
        case 'phone': return {...current, phoneNumber: e.value};
        case 'email': return {...current, email: e.value};
        case 'address': return {...current, address: e.value, city: e.city, state: e.state, postalCode: e.postalCode};
      }
    });
  };

  /** A picked suggestion. The client's own address also takes the point; another entry's row has none. */
  const pickAddress = (key: string, parsed: ParsedAddress) => {
    changeEntry(key, entry => withParsedAddress(entry, parsed));
    if (!isMainKey(key)) return;
    setFields(current => ({...current, lat: parsed.lat ?? current.lat, lng: parsed.lng ?? current.lng}));
    // A suggestion without a point is looked up on save like a typed line.
    if (parsed.lat !== undefined && parsed.lng !== undefined) {
      pinnedLine.current = addressLine(withParsedAddress(mainEntry(fields, 'address'), parsed));
    }
  };

  /** A title belongs to its row, so every entry the row holds takes it. */
  const changeTitle = (key: string, title: string) => setEntries(list => {
    const rowId = list.find(entry => entry.key === key)?.rowId ?? null;
    return list.map(entry => entry.key === key || (rowId !== null && entry.rowId === rowId) ? {...entry, title} : entry);
  });

  /** An empty entry to type straight into. Nothing is sent: Save commits it, and drops it if it is still empty. */
  const addEntry = (kind: ContactKind) =>
    setEntries(list => [...list, {key: `new-${newEntries.current++}`, rowId: null, kind, value: '', city: '', state: '', postalCode: '', title: ''}]);

  /**
   * A phone or email added from a send block (estimate, invoice; Android's addSendRecipient): saved to
   * Additional Client Info at once, the row the profile's Save would create, because the send block is
   * not behind Edit / Save. Appended, not re-read, so an edit in progress on the card survives. The new
   * row, or undefined when the save failed (the error shows as any other, and what was typed stays).
   */
  const addContact = async (entry: ContactEntry): Promise<ClientContactDto | undefined> => {
    const request = clientId === undefined ? undefined : planContactCommit([], [entry]).creates[0];
    if (clientId === undefined || !request) return undefined;
    try {
      const created = await createClientContact(navigate, clientId, request);
      setContacts(rows => [...rows, created]);
      setEntries(list => [...list, ...entriesFrom([created])]);
      return created;
    } catch (e) {
      if (isAuthRedirectError(e)) return undefined;
      logError('Error adding a recipient', {clientId}, e as Error);
      onError("Couldn't add the recipient. Try again.");
      return undefined;
    }
  };

  // ---- Rating ---------------------------------------------------------------------------------

  /** A star sets that many; the current rating's star clears it, so a slip undoes without another control. */
  const tapStar = (stars: number) => {
    if (!client || !rating) return;
    const before = rating;
    const next = before.rating === stars ? null : stars;
    setRating({...before, rating: next});
    setClientRating(navigate, client.id, next)
      .then(setRating)
      .catch(e => {
        setRating(before);
        if (!isAuthRedirectError(e)) onError('Failed to save the rating');
      });
  };

  /** Saves the private review; blank clears it. */
  const saveReview = async (text: string): Promise<boolean> => {
    if (!client) return false;
    try {
      setRating(await setClientReview(navigate, client.id, text.trim() || null));
      return true;
    } catch (e) {
      if (!isAuthRedirectError(e)) onError('Failed to save the review');
      return false;
    }
  };

  return {
    client, setClient, fields, setFields, contacts, entries, contactsRead, rating, loadError,
    draft, settled, locate, persistClient, commitEntries, saveAll,
    changeEntry, pickAddress, changeTitle, addEntry, addContact, tapStar, saveReview,
  };
};
